import { FileInput } from "@/services/fobizzToolsApi";
import type { TUppyUploadError } from "@/types";
import AwsS3, { type AwsBody } from "@uppy/aws-s3";
import Uppy from "@uppy/core";

// eslint-disable-next-line
export function useDirectUploadShrine() {
  type Meta = { license: string; key: string };

  async function uploadFile(
    file: File,
    {
      errorHandler,
      successHandler,
      progressHandler,
    }: {
      errorHandler: (error: TUppyUploadError) => void;
      successHandler: (data: FileInput) => void;
      progressHandler: (progress: number) => void;
    }
  ): Promise<void> {
    const uppy = new Uppy<Meta, AwsBody>().use(AwsS3, { endpoint: "" });

    uppy.on("progress", (progress) => {
      progressHandler(progress);
    });

    uppy.on("upload-success", (file, response) => {
      if (!file) {
        errorHandler(new Error("Failed to upload file! No file present."));
        return;
      }

      const location = decodeURIComponent(response.body?.location || "");

      const id = location.match(/\/cache\/(.+)/)?.[1];

      if (!id) {
        errorHandler(new Error("Failed to upload file! No ID found"));
        return;
      }

      successHandler({
        id,
        storage: FileInput.storage.CACHE,
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      });
    });

    uppy.on("error", (error) => {
      errorHandler(error);
      uppy.clear();
    });

    try {
      const fileID = uppy.addFile(file);

      await uppy.upload();

      uppy.removeFile(fileID);
    } catch (error: unknown) {
      uppy.clear();
      console.error(error);
      errorHandler({
        name: (error instanceof Error ? error.name : "UnknownError") as string,
        message: (error instanceof Error
          ? error.message
          : "Unknown error") as string,
      });
    }
  }

  return {
    uploadFile,
  };
}
